/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'
import { MdOutlineCookie } from 'react-icons/md'
import { CookieDialog } from '@/components/cookies/CookieDialog'
import { Button } from '@/components/ui/Button'
import { analyticalConsentAtom } from '@/store/cookies'

/**
 * The cookie consent banner that appears in the bottom left corner of the page. The banner allows the user to customise
 * their cookie preferences. If the user has not made a choice on the cookie consent, the banner will be displayed.
 * Otherwise, a floating cookie icon will be displayed in the bottom left corner of the page.
 */
const CookieBanner: React.FC = () => {
  const t = useTranslations('CookieBanner')
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false)
  const [showCookieBanner, setShowCookieBanner] = useState(false)
  const [analyticalConsent, setAnalyticalConsent] = useAtom(analyticalConsentAtom)

  /**
   * Handle the user's choice on the cookie consent banner.
   */
  const handleCookieConsentChoice = async (decision: boolean) => {
    await setAnalyticalConsent(decision)
  }

  /**
   * Handle the user's choice to open the cookie consent dialog to customise their preferences.
   */
  const handleOpenCookieConsentDialog = () => {
    setPreferencesDialogOpen(true)
  }

  /**
   * Show the cookie banner if the user has not made a choice on the cookie consent.
   */
  useEffect(() => {
    setShowCookieBanner(analyticalConsent === undefined)
  }, [analyticalConsent])

  return (
    <>
      <CookieDialog open={preferencesDialogOpen} handleClose={() => setPreferencesDialogOpen(false)} />
      {showCookieBanner ? (
        <div className={'fixed bottom-10 left-10 w-[440px]'}>
          <div className={'flex flex-col py-4 px-6 bg-white gap-2.5 rounded-md shadow-md drop-shadow-md'}>
            <h2 className={'text-grey-450 text-lg font-bold'}>{t('weValueYourPrivacy')}</h2>
            <p className={'text-sm text-grey-450 leading-6'}>{t('weUseCookiesTo')}</p>
            <div className={'flex flex-row justify-start items-center gap-2'}>
              <Button variant={'grey'} onClick={handleOpenCookieConsentDialog}>
                {t('customiseButtonText')}
              </Button>
              <Button variant={'grey'} onClick={() => handleCookieConsentChoice(false)}>
                {t('rejectAllButtonText')}
              </Button>
              <Button variant={'secondary'} onClick={() => handleCookieConsentChoice(true)}>
                {t('acceptAllButtonText')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={'fixed bottom-5 left-5 rounded-full bg-blue-800 p-2 cursor-pointer'}
          onClick={handleOpenCookieConsentDialog}
        >
          <MdOutlineCookie className={'text-white text-4xl'} />
        </div>
      )}
    </>
  )
}

export { CookieBanner }
